/**
 * Item resolution: predicates in, a flat renderable list out.
 *
 * Everything conditional about an item is decided here and nowhere else. The
 * renderer receives `ResolvedItem`s whose `disabled`, `checked` and `label` are
 * already plain values, so it never has to call user code while building DOM.
 *
 * This module touches no DOM. It is pure apart from the user callbacks it
 * invokes, and it invokes each of those exactly once per resolution.
 */

import type { Ctx, Item, ItemsResolver, Labeller, Meta, ResolvedItem } from './types'

/** Resolve a `Labeller` to the string the renderer will print. */
export function resolveLabel(label: Labeller, meta: Meta, ctx: Ctx): string {
  return typeof label === 'function' ? label(meta, ctx) : label
}

/** Resolve a `boolean | Predicate` flag. Called at most once per flag per item. */
function resolveFlag(
  flag: boolean | ((meta: Meta, ctx: Ctx) => boolean) | undefined,
  meta: Meta,
  ctx: Ctx,
  fallback: boolean,
): boolean {
  if (flag === undefined) return fallback

  return typeof flag === 'function' ? flag(meta, ctx) : flag
}

/** Normalise `string | string[] | undefined` to a list of non empty class names. */
function resolveClass(value: string | string[] | undefined): string[] {
  if (value === undefined) return []

  const parts = Array.isArray(value) ? value : value.split(/\s+/)

  return parts.filter((part) => part.length > 0)
}

/**
 * Whether an item's `items` field would open a panel with something in it.
 *
 * An array is resolved so a submenu whose every child is hidden reports false:
 * an arrow that opens an empty panel is worse than no arrow. A resolver
 * function is taken on trust, because running it here would call user code at
 * render time and, if it is async, could not be answered synchronously anyway.
 */
function hasRenderableSubmenu(
  items: Item[] | ItemsResolver | undefined,
  meta: Meta,
  ctx: Ctx,
): boolean {
  if (items === undefined) return false
  if (typeof items === 'function') return true

  return resolveItems(items, meta, ctx).length > 0
}

/**
 * Resolve one item, or `null` when it is hidden.
 *
 * Throws are the caller's problem to catch: see `resolveItems`.
 */
function resolveItem(item: Item, meta: Meta, ctx: Ctx): ResolvedItem | null {
  if (item.type === 'separator') return { kind: 'separator' }
  if (item.type === 'custom') return { kind: 'custom', render: item.render }
  if (item.type === 'header') {
    return { kind: 'header', label: resolveLabel(item.label, meta, ctx) }
  }

  // `hidden` is evaluated first and short circuits: an item nobody will see
  // must not run the rest of its user code.
  if (resolveFlag(item.hidden, meta, ctx, false)) return null

  const label = resolveLabel(item.label, meta, ctx)
  const disabled = resolveFlag(item.disabled, meta, ctx, false)
  const classes = resolveClass(item.class)

  if (item.type === 'checkbox') {
    return {
      kind: 'checkbox',
      label,
      checked: resolveFlag(item.checked, meta, ctx, false),
      disabled,
      class: classes,
      variant: item.variant,
      icon: item.icon,
      hint: item.hint,
      source: item,
      id: item.id,
    }
  }

  if (item.type === 'radio') {
    return {
      kind: 'radio',
      label,
      group: item.group,
      // A radio has no `checked` field of its own: the group name is a key in
      // `meta` and the item is checked when that key holds its value. That is
      // what makes a radio group work off the row's own data with no wiring.
      checked: Object.is(meta[item.group], item.value),
      disabled,
      class: classes,
      variant: item.variant,
      icon: item.icon,
      hint: item.hint,
      source: item,
      id: item.id,
    }
  }

  return {
    kind: 'item',
    label,
    icon: item.icon,
    variant: item.variant,
    class: classes,
    hint: item.hint,
    disabled,
    hasSubmenu: hasRenderableSubmenu(item.items, meta, ctx),
    source: item,
    id: item.id,
  }
}

/** One pass of the separator rules over the list as it currently stands. */
function collapseOnce(items: ResolvedItem[]): ResolvedItem[] {
  return items.filter((item, index) => {
    if (item.kind !== 'separator') return true

    const isFirst = index === 0
    const isLast = index === items.length - 1
    const followsSeparator = items[index - 1]?.kind === 'separator'

    return !isFirst && !isLast && !followsSeparator
  })
}

/**
 * Drop every separator that is first, last, or immediately after another.
 *
 * Runs to a fixed point rather than in a single pass, because removing a
 * separator can strand the one before it: `[item, sep, sep, sep]` loses two
 * separators on the first pass and only then is the survivor trailing.
 */
export function collapseSeparators(items: ResolvedItem[]): ResolvedItem[] {
  let current = items

  for (;;) {
    const next = collapseOnce(current)

    if (next.length === current.length) return next

    current = next
  }
}

/**
 * Resolve a list of items for one open of the menu.
 *
 * Hidden items are dropped, predicates are evaluated once each, labels and
 * classes are normalised, and the separators left dangling by all of that are
 * collapsed away.
 *
 * An item whose user code throws is treated as hidden and nothing is
 * re-thrown. Failing closed is deliberate: an item whose guard could not be
 * evaluated might be one the user is not allowed to act on, so it is safer to
 * drop it than to offer it.
 */
export function resolveItems(items: Item[], meta: Meta, ctx: Ctx): ResolvedItem[] {
  const resolved: ResolvedItem[] = []

  for (const item of items) {
    try {
      const result = resolveItem(item, meta, ctx)

      if (result !== null) resolved.push(result)
    } catch (error) {
      console.error('[context-menu] item resolution failed, hiding the item', error, item)
    }
  }

  return collapseSeparators(resolved)
}
